// src/pages/vendor/VendorSponsoredDeals.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db, auth } from "../../firebase";

const SPONSOR_PACKS = [
  {
    id: "shop_3_days",
    type: "shop",
    title: "Boutique sponsorisée · 3 jours",
    durationDays: 3,
    amount: 5000,
    pricingMode: "package",
    objective: "visibility",
    targetPlacement: "homepage_featured_shops",
    description:
      "Votre boutique est mise en avant dans la section des boutiques sponsorisées.",
  },
  {
    id: "shop_7_days",
    type: "shop",
    title: "Boutique sponsorisée · 7 jours",
    durationDays: 7,
    amount: 10000,
    pricingMode: "package",
    objective: "sales",
    targetPlacement: "homepage_featured_shops",
    description:
      "Plus de visibilité pendant une semaine pour attirer de nouveaux clients.",
  },
  {
    id: "product_1_3_days",
    type: "product",
    title: "1 produit sponsorisé · 3 jours",
    durationDays: 3,
    amount: 2000,
    pricingMode: "package",
    objective: "sales",
    targetPlacement: "homepage_products",
    maxProducts: 1,
    description:
      "Mettez un produit en avant pour générer rapidement des clics et des ventes.",
  },
  {
    id: "product_3_7_days",
    type: "product",
    title: "3 produits sponsorisés · 7 jours",
    durationDays: 7,
    amount: 5000,
    pricingMode: "package",
    objective: "sales",
    targetPlacement: "homepage_products",
    maxProducts: 3,
    description:
      "Idéal pour booster une petite sélection de produits pendant 7 jours.",
  },
  {
    id: "product_5_14_days",
    type: "product",
    title: "5 produits sponsorisés · 14 jours",
    durationDays: 14,
    amount: 10000,
    pricingMode: "package",
    objective: "sales",
    targetPlacement: "category_top",
    maxProducts: 5,
    description:
      "Formule plus agressive pour pousser plusieurs produits sur une durée plus longue.",
  },
  {
    id: "custom_premium",
    type: "banner",
    title: "Campagne premium négociée",
    durationDays: 7,
    amount: 0,
    pricingMode: "negotiated",
    objective: "visibility",
    targetPlacement: "homepage_banner",
    description:
      "Pour les grandes campagnes, bannières premium, deals spéciaux et campagnes négociées avec l’équipe GoShop.",
  },
];

function formatMoney(v) {
  const n = Number(v || 0);
  return `${n.toLocaleString("fr-FR")} FCFA`;
}

function formatDateSafe(value) {
  if (!value) return "—";
  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleDateString("fr-FR");
    }
    return new Date(value).toLocaleDateString("fr-FR");
  } catch {
    return "—";
  }
}

function statusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return "En attente";
  if (s === "approved") return "Approuvée";
  if (s === "rejected") return "Refusée";
  if (s === "active") return "Active";
  if (s === "expired") return "Expirée";
  return status || "—";
}

function paymentLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return "Paiement en attente";
  if (s === "paid") return "Payée";
  if (s === "failed") return "Paiement échoué";
  if (s === "not_required") return "Non requis";
  return status || "—";
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "approved" || s === "active") return "vsd-badge vsd-badge-success";
  if (s === "rejected" || s === "failed" || s === "expired") {
    return "vsd-badge vsd-badge-danger";
  }
  return "vsd-badge vsd-badge-warning";
}

export default function VendorSponsoredDeals({ userDoc }) {
  const firebaseUser = auth.currentUser;
  const vendorId = userDoc?.uid || firebaseUser?.uid || "";
  const vendorName =
    userDoc?.shopName ||
    userDoc?.displayName ||
    firebaseUser?.displayName ||
    firebaseUser?.email ||
    "Ma boutique";

  const vendorEmail = userDoc?.email || firebaseUser?.email || "";

  const [products, setProducts] = useState([]);
  const [myDeals, setMyDeals] = useState([]);

  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedPackId, setSelectedPackId] = useState(SPONSOR_PACKS[0].id);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [customTitle, setCustomTitle] = useState("");
  const [description, setDescription] = useState("");
  const [objective, setObjective] = useState("sales");
  const [targetPlacement, setTargetPlacement] = useState("homepage_products");
  const [adsEnabled, setAdsEnabled] = useState(true);
  const [adsBudget, setAdsBudget] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [adminNoteHint, setAdminNoteHint] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const selectedPack = useMemo(() => {
    return SPONSOR_PACKS.find((p) => p.id === selectedPackId) || SPONSOR_PACKS[0];
  }, [selectedPackId]);

  useEffect(() => {
    if (!vendorId) {
      setLoadingProducts(false);
      setLoadingDeals(false);
      return;
    }

    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, "productsShop"),
            where("vendorId", "==", vendorId),
            orderBy("__name__", "desc")
          )
        );

        const items = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setProducts(items);
      } catch (err) {
        console.error("Erreur chargement produits sponsorisables :", err);
      } finally {
        setLoadingProducts(false);
      }
    };

    const loadDeals = async () => {
      setLoadingDeals(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, "vendorSponsoredDeals"),
            where("vendorId", "==", vendorId),
            orderBy("__name__", "desc")
          )
        );

        const items = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setMyDeals(items);
      } catch (err) {
        console.error("Erreur chargement deals sponsorisés :", err);
      } finally {
        setLoadingDeals(false);
      }
    };

    loadProducts();
    loadDeals();
  }, [vendorId]);

  useEffect(() => {
    setError("");
    setInfo("");
    setSelectedProductIds([]);
    setObjective(selectedPack.objective || "sales");
    setTargetPlacement(selectedPack.targetPlacement || "homepage_products");
  }, [selectedPack]);

  const availableProducts = useMemo(() => {
    return products.filter((p) => {
      const status = String(p.validationStatus || "").toLowerCase();
      return status === "validated" || p.isValidated === true;
    });
  }, [products]);

  const canSubmitProducts =
    selectedPack.type !== "product" ||
    (selectedProductIds.length > 0 &&
      selectedProductIds.length <= Number(selectedPack.maxProducts || 0));

  const handleToggleProduct = (productId) => {
    setSelectedProductIds((prev) => {
      const exists = prev.includes(productId);

      if (exists) {
        return prev.filter((id) => id !== productId);
      }

      const max = Number(selectedPack.maxProducts || 0);
      if (selectedPack.type === "product" && max > 0 && prev.length >= max) {
        return prev;
      }

      return [...prev, productId];
    });
  };

  const resetForm = () => {
    setSelectedPackId(SPONSOR_PACKS[0].id);
    setSelectedProductIds([]);
    setCustomTitle("");
    setDescription("");
    setObjective("sales");
    setTargetPlacement("homepage_products");
    setAdsEnabled(true);
    setAdsBudget("");
    setCategoryId("");
    setAdminNoteHint("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (!vendorId) {
      setError("Impossible d’identifier votre boutique.");
      return;
    }

    if (selectedPack.type === "product" && !canSubmitProducts) {
      setError("Veuillez sélectionner le bon nombre de produits pour cette formule.");
      return;
    }

    if (selectedPack.type === "product" && availableProducts.length === 0) {
      setError("Vous devez avoir au moins un produit validé pour sponsoriser des produits.");
      return;
    }

    if (selectedPack.pricingMode === "negotiated" && !customTitle.trim()) {
      setError("Veuillez donner un titre à votre campagne premium.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        vendorId,
        vendorName,
        vendorEmail: vendorEmail || null,

        type: selectedPack.type,
        title:
          selectedPack.pricingMode === "negotiated"
            ? customTitle.trim()
            : selectedPack.title,
        description:
          description.trim() || selectedPack.description || null,

        objective,
        targetPlacement,
        targetCategoryId: categoryId.trim() || null,
        productIds: selectedPack.type === "product" ? selectedProductIds : [],
        categoryId: categoryId.trim() || null,

        durationDays: Number(selectedPack.durationDays || 0),
        amountRequested: Number(selectedPack.amount || 0),
        amountApproved: null,
        currency: "XOF",

        pricingMode: selectedPack.pricingMode,
        paymentStatus: "pending",
        status: "pending",

        creativeType:
          selectedPack.type === "banner" ? "custom_banner" : "existing_product_assets",

        bannerDesktopUrl: null,
        bannerMobileUrl: null,
        imageUrl: null,

        adsEnabled,
        adsPlatform: adsEnabled ? "meta" : null,
        adsBudget: adsEnabled ? Number(adsBudget || 0) : 0,
        adsStatus: adsEnabled ? "pending" : "not_required",
        adsCampaignId: null,

        startAt: null,
        endAt: null,

        adminNote: null,
        vendorNote: adminNoteHint.trim() || null,

        impressions: 0,
        clicks: 0,
        conversions: 0,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "vendorSponsoredDeals"), payload);

      setInfo(
        "Votre demande de sponsoring a été envoyée avec succès. L’équipe GoShop va l’étudier."
      );

      resetForm();

      const snap = await getDocs(
        query(
          collection(db, "vendorSponsoredDeals"),
          where("vendorId", "==", vendorId),
          orderBy("__name__", "desc")
        )
      );

      setMyDeals(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    } catch (err) {
      console.error("Erreur envoi demande sponsoring :", err);
      setError("Impossible d’envoyer la demande de sponsoring.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="vsd-page">
      <main className="vsd-main">
        <header className="vsd-hero">
          <div className="vsd-hero-copy">
            <span className="vsd-kicker">Boost Ventes GoShop</span>
            <h1 className="vsd-title">Lancez une campagne sponsorisée</h1>
            <p className="vsd-subtitle">
              Mettez en avant votre boutique ou vos produits pour gagner en
              visibilité, générer plus de clics et vendre davantage sur GoShop.
            </p>
          </div>

          <div className="vsd-hero-card">
            <div className="vsd-hero-stat">
              <span>Votre boutique</span>
              <strong>{vendorName}</strong>
            </div>
            <div className="vsd-hero-stat">
              <span>Produits validés</span>
              <strong>{availableProducts.length}</strong>
            </div>
            <div className="vsd-hero-stat">
              <span>Demandes sponsorisées</span>
              <strong>{myDeals.length}</strong>
            </div>
          </div>
        </header>

        {error && <div className="vsd-alert vsd-alert-error">{error}</div>}
        {info && <div className="vsd-alert vsd-alert-success">{info}</div>}

        <section className="vsd-layout">
          <div className="vsd-left">
            <section className="vsd-card">
              <div className="vsd-card-head">
                <h2>Choisissez votre formule</h2>
                <p>
                  Sélectionnez une offre adaptée à votre objectif commercial.
                </p>
              </div>

              <div className="vsd-pack-grid">
                {SPONSOR_PACKS.map((pack) => {
                  const isActive = selectedPackId === pack.id;
                  return (
                    <button
                      type="button"
                      key={pack.id}
                      className={`vsd-pack-card ${isActive ? "is-active" : ""}`}
                      onClick={() => setSelectedPackId(pack.id)}
                    >
                      <div className="vsd-pack-top">
                        <span className="vsd-pack-type">
                          {pack.type === "shop"
                            ? "Boutique"
                            : pack.type === "product"
                            ? "Produit"
                            : "Premium"}
                        </span>
                        <strong className="vsd-pack-price">
                          {pack.pricingMode === "negotiated"
                            ? "Prix négocié"
                            : formatMoney(pack.amount)}
                        </strong>
                      </div>

                      <h3 className="vsd-pack-title">{pack.title}</h3>
                      <p className="vsd-pack-desc">{pack.description}</p>

                      <div className="vsd-pack-meta">
                        <span>{pack.durationDays} jour(s)</span>
                        <span>
                          {pack.type === "product" && pack.maxProducts
                            ? `${pack.maxProducts} produit(s) max`
                            : pack.targetPlacement}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="vsd-card">
              <div className="vsd-card-head">
                <h2>Envoyer une demande</h2>
                <p>
                  Décrivez votre campagne et envoyez-la à l’équipe GoShop.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="vsd-form">
                <div className="vsd-summary-box">
                  <div>
                    <span className="vsd-summary-label">Formule sélectionnée</span>
                    <strong>{selectedPack.title}</strong>
                  </div>
                  <div>
                    <span className="vsd-summary-label">Budget demandé</span>
                    <strong>
                      {selectedPack.pricingMode === "negotiated"
                        ? "À négocier"
                        : formatMoney(selectedPack.amount)}
                    </strong>
                  </div>
                  <div>
                    <span className="vsd-summary-label">Durée</span>
                    <strong>{selectedPack.durationDays} jour(s)</strong>
                  </div>
                </div>

                {selectedPack.pricingMode === "negotiated" && (
                  <div className="vsd-field">
                    <label>Titre de la campagne premium</label>
                    <input
                      type="text"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      placeholder="Ex : Campagne Ramadan / Bannière page d’accueil"
                    />
                  </div>
                )}

                <div className="vsd-form-grid">
                  <div className="vsd-field">
                    <label>Objectif</label>
                    <select
                      value={objective}
                      onChange={(e) => setObjective(e.target.value)}
                    >
                      <option value="sales">Ventes</option>
                      <option value="traffic">Trafic</option>
                      <option value="visibility">Visibilité</option>
                    </select>
                  </div>

                  <div className="vsd-field">
                    <label>Emplacement souhaité</label>
                    <select
                      value={targetPlacement}
                      onChange={(e) => setTargetPlacement(e.target.value)}
                    >
                      <option value="homepage_featured_shops">
                        Homepage · Boutiques sponsorisées
                      </option>
                      <option value="homepage_products">
                        Homepage · Produits sponsorisés
                      </option>
                      <option value="category_top">
                        Haut de catégorie
                      </option>
                      <option value="search_top">
                        Résultats de recherche
                      </option>
                      <option value="homepage_banner">
                        Bannière homepage
                      </option>
                    </select>
                  </div>

                  <div className="vsd-field">
                    <label>Catégorie ciblée (optionnel)</label>
                    <input
                      type="text"
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                      placeholder="Ex : beauty"
                    />
                  </div>

                  <div className="vsd-field">
                    <label>Budget pub Meta (optionnel)</label>
                    <input
                      type="number"
                      min="0"
                      value={adsBudget}
                      onChange={(e) => setAdsBudget(e.target.value)}
                      placeholder="Ex : 5000"
                      disabled={!adsEnabled}
                    />
                  </div>
                </div>

                <div className="vsd-switch-row">
                  <label className="vsd-switch">
                    <input
                      type="checkbox"
                      checked={adsEnabled}
                      onChange={(e) => setAdsEnabled(e.target.checked)}
                    />
                    <span>Je souhaite aussi une diffusion publicitaire Meta</span>
                  </label>
                </div>

                <div className="vsd-field">
                  <label>Description / consignes</label>
                  <textarea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Expliquez votre objectif : produit à pousser, période de promo, cible souhaitée, arguments commerciaux..."
                  />
                </div>

                <div className="vsd-field">
                  <label>Message pour l’équipe GoShop (optionnel)</label>
                  <input
                    type="text"
                    value={adminNoteHint}
                    onChange={(e) => setAdminNoteHint(e.target.value)}
                    placeholder="Ex : je veux cibler la catégorie beauté et pousser mes nouveautés"
                  />
                </div>

                {selectedPack.type === "product" && (
                  <div className="vsd-products-box">
                    <div className="vsd-products-head">
                      <div>
                        <h3>Choisissez les produits à sponsoriser</h3>
                        <p>
                          Maximum : {selectedPack.maxProducts} produit(s) pour cette formule.
                        </p>
                      </div>
                      <span className="vsd-counter">
                        {selectedProductIds.length}/{selectedPack.maxProducts}
                      </span>
                    </div>

                    {loadingProducts ? (
                      <div className="vsd-empty">Chargement des produits...</div>
                    ) : availableProducts.length === 0 ? (
                      <div className="vsd-empty">
                        Aucun produit validé disponible pour une campagne sponsorisée.
                      </div>
                    ) : (
                      <div className="vsd-products-grid">
                        {availableProducts.map((product) => {
                          const selected = selectedProductIds.includes(product.id);
                          const price =
                            product.SalePrice ??
                            product.salePrice ??
                            product.price ??
                            product.Cass_Stock_Price ??
                            0;

                          return (
                            <button
                              type="button"
                              key={product.id}
                              className={`vsd-product-item ${selected ? "is-selected" : ""}`}
                              onClick={() => handleToggleProduct(product.id)}
                            >
                              <div className="vsd-product-thumb">
                                {product.ImageUrl ? (
                                  <img src={product.ImageUrl} alt={product.name || "Produit"} />
                                ) : (
                                  <span>{(product.shortName || product.name || "P").charAt(0)}</span>
                                )}
                              </div>

                              <div className="vsd-product-body">
                                <strong>{product.shortName || product.name || "Produit"}</strong>
                                <span>{formatMoney(price)}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div className="vsd-submit-row">
                  <button
                    type="submit"
                    className="vsd-submit-btn"
                    disabled={
                      submitting ||
                      (selectedPack.type === "product" && !canSubmitProducts)
                    }
                  >
                    {submitting ? "Envoi..." : "Envoyer la demande"}
                  </button>

                  <p className="vsd-submit-note">
                    Votre demande sera examinée par l’équipe GoShop avant activation.
                  </p>
                </div>
              </form>
            </section>
          </div>

          <aside className="vsd-right">
            <section className="vsd-card">
              <div className="vsd-card-head">
                <h2>Comment ça marche ?</h2>
              </div>

              <ol className="vsd-steps">
                <li>Vous choisissez une formule de sponsoring.</li>
                <li>Vous indiquez vos produits ou votre objectif.</li>
                <li>GoShop examine et approuve la demande.</li>
                <li>Le paiement est validé.</li>
                <li>Votre campagne est activée et mise en avant.</li>
              </ol>
            </section>

            <section className="vsd-card">
              <div className="vsd-card-head">
                <h2>Historique de vos campagnes</h2>
                <p>Suivez vos demandes sponsorisées.</p>
              </div>

              {loadingDeals ? (
                <div className="vsd-empty">Chargement des campagnes...</div>
              ) : myDeals.length === 0 ? (
                <div className="vsd-empty">Aucune campagne sponsorisée pour le moment.</div>
              ) : (
                <div className="vsd-history-list">
                  {myDeals.map((deal) => (
                    <article key={deal.id} className="vsd-history-item">
                      <div className="vsd-history-top">
                        <h3>{deal.title || "Campagne sponsorisée"}</h3>
                        <span className={statusClass(deal.status)}>
                          {statusLabel(deal.status)}
                        </span>
                      </div>

                      <p className="vsd-history-meta">
                        Type : {deal.type || "—"} · Budget :{" "}
                        {deal.amountApproved
                          ? formatMoney(deal.amountApproved)
                          : deal.amountRequested
                          ? formatMoney(deal.amountRequested)
                          : "—"}
                      </p>

                      <p className="vsd-history-meta">
                        Paiement :{" "}
                        <span className={statusClass(deal.paymentStatus)}>
                          {paymentLabel(deal.paymentStatus)}
                        </span>
                      </p>

                      <p className="vsd-history-meta">
                        Début : {formatDateSafe(deal.startAt)} · Fin :{" "}
                        {formatDateSafe(deal.endAt)}
                      </p>

                      {deal.adminNote && (
                        <p className="vsd-admin-note">
                          Note admin : {deal.adminNote}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </section>
          </aside>
        </section>

        <style>{`
          .vsd-page{
            min-height:100vh;
            background:linear-gradient(180deg,#f8fafc 0%, #f1f5f9 100%);
            padding:24px 16px 42px;
            display:flex;
            justify-content:center;
          }

          .vsd-main{
            width:100%;
            max-width:1200px;
            display:flex;
            flex-direction:column;
            gap:18px;
          }

          .vsd-hero{
            display:grid;
            grid-template-columns:minmax(0,1.4fr) minmax(280px,.7fr);
            gap:16px;
            align-items:stretch;
          }

          .vsd-hero-copy,
          .vsd-hero-card,
          .vsd-card{
            background:#fff;
            border:1px solid #e5e7eb;
            border-radius:24px;
            box-shadow:0 16px 36px rgba(15,23,42,.06);
          }

          .vsd-hero-copy{
            padding:24px;
          }

          .vsd-kicker{
            display:inline-flex;
            align-items:center;
            padding:6px 10px;
            border-radius:999px;
            background:#ede9fe;
            color:#5b21b6;
            font-size:11px;
            font-weight:900;
            letter-spacing:.08em;
            text-transform:uppercase;
            margin-bottom:10px;
          }

          .vsd-title{
            margin:0 0 8px;
            font-size:30px;
            line-height:1.15;
            color:#0f172a;
            font-weight:900;
          }

          .vsd-subtitle{
            margin:0;
            color:#475569;
            font-size:14px;
            max-width:760px;
          }

          .vsd-hero-card{
            padding:18px;
            display:flex;
            flex-direction:column;
            justify-content:center;
            gap:12px;
          }

          .vsd-hero-stat{
            border:1px solid #e5e7eb;
            border-radius:18px;
            padding:12px 14px;
            background:#f8fafc;
          }

          .vsd-hero-stat span{
            display:block;
            font-size:12px;
            color:#64748b;
            margin-bottom:4px;
          }

          .vsd-hero-stat strong{
            color:#0f172a;
            font-size:18px;
            font-weight:900;
          }

          .vsd-alert{
            padding:10px 12px;
            border-radius:14px;
            font-size:13px;
            font-weight:700;
          }

          .vsd-alert-error{
            background:#fef2f2;
            color:#b91c1c;
            border:1px solid #fecaca;
          }

          .vsd-alert-success{
            background:#ecfdf3;
            color:#166534;
            border:1px solid #bbf7d0;
          }

          .vsd-layout{
            display:grid;
            grid-template-columns:minmax(0,1.55fr) minmax(320px,.75fr);
            gap:18px;
            align-items:start;
          }

          .vsd-left,
          .vsd-right{
            display:flex;
            flex-direction:column;
            gap:18px;
          }

          .vsd-card{
            padding:18px;
          }

          .vsd-card-head{
            margin-bottom:14px;
          }

          .vsd-card-head h2{
            margin:0 0 4px;
            color:#0f172a;
            font-size:20px;
            font-weight:900;
          }

          .vsd-card-head p{
            margin:0;
            color:#64748b;
            font-size:13px;
          }

          .vsd-pack-grid{
            display:grid;
            grid-template-columns:repeat(2,minmax(0,1fr));
            gap:12px;
          }

          .vsd-pack-card{
            border:1px solid #e5e7eb;
            background:#fff;
            border-radius:20px;
            padding:14px;
            text-align:left;
            cursor:pointer;
            transition:.18s ease;
            display:flex;
            flex-direction:column;
            gap:8px;
          }

          .vsd-pack-card:hover{
            transform:translateY(-1px);
            box-shadow:0 12px 28px rgba(15,23,42,.08);
          }

          .vsd-pack-card.is-active{
            border-color:#2563eb;
            background:linear-gradient(180deg,#eff6ff 0%, #ffffff 100%);
            box-shadow:0 0 0 2px rgba(37,99,235,.08);
          }

          .vsd-pack-top{
            display:flex;
            justify-content:space-between;
            gap:8px;
            align-items:flex-start;
          }

          .vsd-pack-type{
            display:inline-flex;
            align-items:center;
            padding:4px 8px;
            border-radius:999px;
            background:#f8fafc;
            border:1px solid #e5e7eb;
            color:#334155;
            font-size:11px;
            font-weight:900;
          }

          .vsd-pack-price{
            color:#16a34a;
            font-size:14px;
            font-weight:900;
          }

          .vsd-pack-title{
            margin:0;
            font-size:15px;
            color:#0f172a;
            font-weight:900;
          }

          .vsd-pack-desc{
            margin:0;
            font-size:12px;
            color:#64748b;
          }

          .vsd-pack-meta{
            display:flex;
            flex-wrap:wrap;
            gap:8px;
            font-size:11px;
            color:#475569;
            font-weight:700;
          }

          .vsd-form{
            display:flex;
            flex-direction:column;
            gap:14px;
          }

          .vsd-summary-box{
            display:grid;
            grid-template-columns:repeat(3,minmax(0,1fr));
            gap:10px;
            padding:12px;
            border-radius:18px;
            background:#f8fafc;
            border:1px solid #e5e7eb;
          }

          .vsd-summary-label{
            display:block;
            font-size:11px;
            color:#64748b;
            margin-bottom:3px;
          }

          .vsd-summary-box strong{
            color:#0f172a;
            font-size:14px;
          }

          .vsd-form-grid{
            display:grid;
            grid-template-columns:repeat(2,minmax(0,1fr));
            gap:12px 14px;
          }

          .vsd-field{
            display:flex;
            flex-direction:column;
            gap:6px;
          }

          .vsd-field label{
            font-size:12px;
            font-weight:800;
            color:#374151;
          }

          .vsd-field input,
          .vsd-field select,
          .vsd-field textarea{
            width:100%;
            border:1px solid #d1d5db;
            border-radius:14px;
            background:#fff;
            padding:10px 12px;
            font-size:13px;
            color:#111827;
            outline:none;
          }

          .vsd-field input:focus,
          .vsd-field select:focus,
          .vsd-field textarea:focus{
            border-color:#2563eb;
            box-shadow:0 0 0 3px rgba(37,99,235,.08);
          }

          .vsd-switch-row{
            display:flex;
            align-items:center;
          }

          .vsd-switch{
            display:flex;
            gap:10px;
            align-items:center;
            font-size:13px;
            color:#0f172a;
            font-weight:700;
          }

          .vsd-products-box{
            border:1px solid #e5e7eb;
            border-radius:20px;
            padding:14px;
            background:#fafafa;
          }

          .vsd-products-head{
            display:flex;
            justify-content:space-between;
            gap:12px;
            align-items:flex-start;
            margin-bottom:12px;
          }

          .vsd-products-head h3{
            margin:0 0 4px;
            font-size:15px;
            color:#0f172a;
            font-weight:900;
          }

          .vsd-products-head p{
            margin:0;
            font-size:12px;
            color:#64748b;
          }

          .vsd-counter{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            min-width:54px;
            padding:6px 10px;
            border-radius:999px;
            background:#eff6ff;
            color:#1d4ed8;
            font-size:12px;
            font-weight:900;
            border:1px solid #dbeafe;
          }

          .vsd-products-grid{
            display:grid;
            grid-template-columns:repeat(2,minmax(0,1fr));
            gap:10px;
          }

          .vsd-product-item{
            display:flex;
            gap:10px;
            align-items:center;
            text-align:left;
            border:1px solid #e5e7eb;
            background:#fff;
            border-radius:16px;
            padding:10px;
            cursor:pointer;
            transition:.18s ease;
          }

          .vsd-product-item.is-selected{
            border-color:#16a34a;
            background:#f0fdf4;
            box-shadow:0 0 0 2px rgba(22,163,74,.08);
          }

          .vsd-product-thumb{
            width:62px;
            height:62px;
            border-radius:14px;
            overflow:hidden;
            background:#f3f4f6;
            display:flex;
            align-items:center;
            justify-content:center;
            flex-shrink:0;
          }

          .vsd-product-thumb img{
            width:100%;
            height:100%;
            object-fit:cover;
            display:block;
          }

          .vsd-product-thumb span{
            font-size:18px;
            font-weight:900;
            color:#94a3b8;
          }

          .vsd-product-body{
            display:flex;
            flex-direction:column;
            gap:4px;
            min-width:0;
          }

          .vsd-product-body strong{
            font-size:13px;
            color:#0f172a;
            line-height:1.3;
          }

          .vsd-product-body span{
            font-size:12px;
            color:#16a34a;
            font-weight:800;
          }

          .vsd-submit-row{
            display:flex;
            flex-direction:column;
            gap:8px;
          }

          .vsd-submit-btn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:11px 18px;
            border:none;
            border-radius:999px;
            background:linear-gradient(135deg,#2563eb 0%, #1d4ed8 100%);
            color:#fff;
            font-size:14px;
            font-weight:900;
            cursor:pointer;
            box-shadow:0 12px 28px rgba(37,99,235,.22);
          }

          .vsd-submit-btn:hover:not(:disabled){
            background:#0f172a;
          }

          .vsd-submit-btn:disabled{
            opacity:.7;
            cursor:not-allowed;
          }

          .vsd-submit-note{
            margin:0;
            font-size:12px;
            color:#64748b;
          }

          .vsd-steps{
            margin:0;
            padding-left:18px;
            color:#475569;
            font-size:13px;
          }

          .vsd-steps li{
            margin-bottom:8px;
          }

          .vsd-history-list{
            display:flex;
            flex-direction:column;
            gap:10px;
          }

          .vsd-history-item{
            border:1px solid #e5e7eb;
            border-radius:18px;
            padding:12px;
            background:#fafafa;
          }

          .vsd-history-top{
            display:flex;
            justify-content:space-between;
            gap:10px;
            align-items:flex-start;
            margin-bottom:6px;
          }

          .vsd-history-top h3{
            margin:0;
            font-size:14px;
            color:#0f172a;
            font-weight:900;
          }

          .vsd-history-meta{
            margin:4px 0 0;
            font-size:12px;
            color:#64748b;
          }

          .vsd-admin-note{
            margin:8px 0 0;
            font-size:12px;
            color:#b45309;
            font-weight:700;
          }

          .vsd-badge{
            display:inline-flex;
            align-items:center;
            padding:4px 8px;
            border-radius:999px;
            font-size:11px;
            font-weight:900;
            white-space:nowrap;
          }

          .vsd-badge-success{
            background:#dcfce7;
            color:#166534;
          }

          .vsd-badge-warning{
            background:#fef9c3;
            color:#92400e;
          }

          .vsd-badge-danger{
            background:#fee2e2;
            color:#b91c1c;
          }

          .vsd-empty{
            font-size:13px;
            color:#64748b;
            padding:8px 0;
          }

          @media (max-width: 1040px){
            .vsd-layout{
              grid-template-columns:minmax(0,1fr);
            }
          }

          @media (max-width: 900px){
            .vsd-hero{
              grid-template-columns:minmax(0,1fr);
            }

            .vsd-pack-grid,
            .vsd-form-grid,
            .vsd-products-grid,
            .vsd-summary-box{
              grid-template-columns:minmax(0,1fr);
            }
          }

          @media (max-width: 640px){
            .vsd-page{
              padding:18px 12px 34px;
            }

            .vsd-title{
              font-size:24px;
            }

            .vsd-card,
            .vsd-hero-copy,
            .vsd-hero-card{
              border-radius:20px;
            }
          }
        `}</style>
      </main>
    </div>
  );
}